import React, { useState, useEffect } from 'react';
import {
    Show,
    SimpleShowLayout,
    TextField,
    DateField,
    useTranslate,
    EditButton,
    DeleteButton,
    useRecordContext,
    FunctionField,
    useRedirect,
    useDataProvider,
    useNotify,
    useGetOne,
} from 'react-admin';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Alert,
    IconButton,
    Button,
    CircularProgress,
    Fab,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    AttachMoney,
    ElectricBolt,
    Schedule,
    HourglassEmpty,
    EventSeat,
    AdminPanelSettings,
    PersonOutline,
    ArrowBack,
    Info,
    Settings,
    History,
    Update,
    ScheduleSend,
    Edit as EditIcon,
    DeleteOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FeeTemplate, FeeStructure } from '../../types/fee.types';

const tableCellWrapSx = {
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    whiteSpace: 'normal',
    verticalAlign: 'top',
};

const detailsTableSx = {
    tableLayout: 'fixed',
    width: '100%',
    '& .MuiTableCell-root': {
        ...tableCellWrapSx,
        px: { xs: 1, sm: 2 },
        py: { xs: 1, sm: 1.25 },
        fontSize: { xs: '0.75rem', sm: '0.875rem' },
    },
};

const mobileDetailRowSx = {
    py: 1,
    px: 1.25,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'rgba(0, 0, 0, 0.01)',
};

const mobileDetailLabelSx = {
    fontSize: '0.72rem',
    color: 'text.secondary',
    fontWeight: 600,
    mb: 0.5,
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
};

const mobileDetailValueSx = {
    fontSize: '0.875rem',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
};

const PolicyNameField: React.FC<{ policyId: string | null }> = ({ policyId }) => {
    const translate = useTranslate();
    const { data } = useGetOne(
        'reservation-policies',
        { id: policyId as string },
        { enabled: !!policyId }
    );
    if (!policyId) {
        return <span>{translate('resources.fee-templates.reservationFee.useSystemDefault')}</span>;
    }
    return <span>{data?.name ?? policyId}</span>;
};

// Page header component
const PageHeader = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
                <IconButton
                    onClick={() => redirect('/fee-templates')}
                    size="large"
                    sx={{
                        flexShrink: 0,
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        '&:hover': {
                            backgroundColor: '#F5F5F5',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                        },
                    }}
                    title={translate('ra.action.back')}
                >
                    <ArrowBack />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {record?.name}
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    >
                        {translate('resources.fee-templates.show.subtitle')}
                    </Typography>
                </Box>
            </Box>

            {!isMobile && (
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <EditButton
                        label={translate('ra.action.edit')}
                        variant="contained"
                        sx={{
                            backgroundColor: '#00C853',
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            px: 3,
                            py: 1.5,
                            '&:hover': {
                                backgroundColor: '#00A843',
                                boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                            },
                        }}
                    />
                    {!record?.isSystem && (
                        <DeleteButton
                            label={translate('ra.action.delete')}
                            variant="outlined"
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 2,
                                px: 3,
                                py: 1.5,
                                borderColor: '#E0E0E0',
                                '&:hover': {
                                    borderColor: '#D32F2F',
                                    backgroundColor: 'rgba(211, 47, 47, 0.04)',
                                },
                            }}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};

const FeeStructureDisplay = ({ feeStructure }: { feeStructure: FeeStructure }) => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const componentIcons = {
        sessionFee: <AttachMoney />,
        energyFee: <ElectricBolt />,
        timeFee: <Schedule />,
        idleFee: <HourglassEmpty />,
        reservationFee: <EventSeat />,
    };

    const componentLabels = {
        sessionFee: translate('resources.fee-templates.components.sessionFee'),
        energyFee: translate('resources.fee-templates.components.energyFee'),
        timeFee: translate('resources.fee-templates.components.timeFee'),
        idleFee: translate('resources.fee-templates.components.idleFee'),
        reservationFee: translate('resources.fee-templates.components.reservationFee'),
    };

    const enabledComponents = Object.entries(feeStructure.components).filter(([, component]) => component?.enabled);

    if (enabledComponents.length === 0) {
        return (
            <Alert severity="info">
                {translate('resources.fee-templates.show.noEnabledComponents')}
            </Alert>
        );
    }

    return (
        <Box>
            {enabledComponents.map(([key, component]) => (
                <Box key={key} sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        {componentIcons[key as keyof typeof componentIcons]}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                            {componentLabels[key as keyof typeof componentLabels]}
                        </Typography>
                        <Chip
                            label={translate('resources.fee-templates.components.enabled')}
                            size="small"
                            color="success"
                            component="div"
                            clickable={false}
                            sx={{
                                cursor: 'default',
                                maxWidth: '100%',
                                '& .MuiChip-label': {
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                },
                            }}
                        />
                    </Box>
                    <ComponentDetails component={component} componentType={key} />
                </Box>
            ))}

            {feeStructure.specialDates && feeStructure.specialDates.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        {translate('resources.fee-templates.components.specialDates')}
                    </Typography>
                    {isMobile ? (
                        <Box sx={{ display: 'grid', gap: 1 }}>
                            {feeStructure.specialDates.map((specialDate, index) => (
                                <Box key={index} sx={mobileDetailRowSx}>
                                    <Typography sx={mobileDetailLabelSx}>{translate('resources.fee-templates.fields.date')}</Typography>
                                    <Typography sx={mobileDetailValueSx}>{specialDate.date}</Typography>
                                    <Typography sx={{ ...mobileDetailLabelSx, mt: 1 }}>{translate('resources.fee-templates.fields.name')}</Typography>
                                    <Typography sx={mobileDetailValueSx}>{specialDate.name || '-'}</Typography>
                                    <Typography sx={{ ...mobileDetailLabelSx, mt: 1 }}>{translate('resources.fee-templates.fields.rateMultiplier')}</Typography>
                                    <Typography sx={mobileDetailValueSx}>
                                        {specialDate.rateMultiplier
                                            ? `${(specialDate.rateMultiplier * 100).toFixed(0)}%`
                                            : '-'}
                                    </Typography>
                                    <Typography sx={{ ...mobileDetailLabelSx, mt: 1 }}>{translate('resources.fee-templates.fields.components')}</Typography>
                                    <Typography sx={mobileDetailValueSx}>{specialDate.components?.join(', ') || 'All'}</Typography>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                            <Table size="small" sx={detailsTableSx}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{translate('resources.fee-templates.fields.date')}</TableCell>
                                        <TableCell>{translate('resources.fee-templates.fields.name')}</TableCell>
                                        <TableCell>{translate('resources.fee-templates.fields.rateMultiplier')}</TableCell>
                                        <TableCell>{translate('resources.fee-templates.fields.components')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {feeStructure.specialDates.map((specialDate, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{specialDate.date}</TableCell>
                                            <TableCell>{specialDate.name}</TableCell>
                                            <TableCell>
                                                {specialDate.rateMultiplier
                                                    ? `${(specialDate.rateMultiplier * 100).toFixed(0)}%`
                                                    : '-'
                                                }
                                            </TableCell>
                                            <TableCell>{specialDate.components?.join(', ') || 'All'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            )}
        </Box>
    );
};

const ComponentDetails = ({ component, componentType }: { component: any; componentType: string }) => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    switch (componentType) {
        case 'sessionFee':
            return (
                <Box>
                    <Typography sx={mobileDetailValueSx}>
                        <strong>{translate('resources.fee-templates.fields.amount')}:</strong> {component.amount}
                    </Typography>
                    {component.description && (
                        <Typography sx={mobileDetailValueSx}>
                            <strong>{translate('resources.fee-templates.fields.description')}:</strong> {component.description}
                        </Typography>
                    )}
                </Box>
            );

        case 'energyFee':
            return (
                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        {translate('resources.fee-templates.components.energyRates')}
                    </Typography>
                    {isMobile ? (
                        <Box sx={{ display: 'grid', gap: 1 }}>
                            {component.rates?.map((rate: any, index: number) => (
                                <Box key={index} sx={mobileDetailRowSx}>
                                    <Typography sx={mobileDetailLabelSx}>{translate('resources.fee-templates.fields.rateName')}</Typography>
                                    <Typography sx={mobileDetailValueSx}>{rate.name || '-'}</Typography>
                                    <Typography sx={{ ...mobileDetailLabelSx, mt: 1 }}>{translate('resources.fee-templates.fields.rateAmount')}</Typography>
                                    <Typography sx={mobileDetailValueSx}>{rate.rate ?? '-'}</Typography>
                                    <Typography sx={{ ...mobileDetailLabelSx, mt: 1 }}>{translate('resources.fee-templates.fields.unit')}</Typography>
                                    <Typography sx={mobileDetailValueSx}>{rate.unit || '-'}</Typography>
                                    <Typography sx={{ ...mobileDetailLabelSx, mt: 1 }}>{translate('resources.fee-templates.fields.timeWindows')}</Typography>
                                    <Typography sx={mobileDetailValueSx}>
                                        {rate.timeWindows?.length > 0
                                            ? rate.timeWindows.map((tw: any) => `${tw.days.join(', ')}: ${tw.start}-${tw.end}`).join('; ')
                                            : translate('resources.fee-templates.fields.allTimes')}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                            <Table size="small" sx={detailsTableSx}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{translate('resources.fee-templates.fields.rateName')}</TableCell>
                                        <TableCell>{translate('resources.fee-templates.fields.rateAmount')}</TableCell>
                                        <TableCell>{translate('resources.fee-templates.fields.unit')}</TableCell>
                                        <TableCell>{translate('resources.fee-templates.fields.timeWindows')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {component.rates?.map((rate: any, index: number) => (
                                        <TableRow key={index}>
                                            <TableCell>{rate.name}</TableCell>
                                            <TableCell>{rate.rate}</TableCell>
                                            <TableCell>{rate.unit}</TableCell>
                                            <TableCell>
                                                {rate.timeWindows?.length > 0
                                                    ? rate.timeWindows.map((tw: any) =>
                                                        `${tw.days.join(', ')}: ${tw.start}-${tw.end}`
                                                      ).join('; ')
                                                    : translate('resources.fee-templates.fields.allTimes')
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            );

        case 'timeFee':
            return (
                <Box>
                    {component.rates?.map((rate: any, index: number) => (
                        <Typography key={index} sx={mobileDetailValueSx}>
                            <strong>{rate.name ? `${rate.name}: ` : ''}{translate('resources.fee-templates.fields.rate')}:</strong> {rate.rate} per {component.unit || 'minute'}
                        </Typography>
                    ))}
                    {component.freeMinutes > 0 && (
                        <Typography sx={mobileDetailValueSx}>
                            <strong>{translate('resources.fee-templates.fields.freeMinutes')}:</strong> {component.freeMinutes}
                        </Typography>
                    )}
                    {component.maxChargeMinutes > 0 && (
                        <Typography sx={mobileDetailValueSx}>
                            <strong>{translate('resources.fee-templates.fields.maxChargeMinutes')}:</strong> {component.maxChargeMinutes}
                        </Typography>
                    )}
                </Box>
            );

        case 'idleFee':
            return (
                <Box>
                    <Typography sx={mobileDetailValueSx}>
                        <strong>{translate('resources.fee-templates.fields.graceMinutes')}:</strong> {component.graceMinutes}
                    </Typography>
                    <Typography sx={mobileDetailValueSx}>
                        <strong>{translate('resources.fee-templates.fields.rate')}:</strong> {component.rate} per {component.unit}
                    </Typography>
                    {component.maxPerSession && (
                        <Typography sx={mobileDetailValueSx}>
                            <strong>{translate('resources.fee-templates.fields.maxPerSession')}:</strong> {component.maxPerSession}
                        </Typography>
                    )}
                </Box>
            );

        case 'reservationFee':
            return (
                <Box>
                    <Typography sx={mobileDetailValueSx}>
                        <strong>{translate('resources.fee-templates.fields.amount')}:</strong> {component.amount} HKD
                    </Typography>
                    <Typography sx={mobileDetailValueSx}>
                        <strong>{translate('resources.fee-templates.reservationFee.policy')}:</strong>{' '}
                        <PolicyNameField policyId={component.policyId ?? null} />
                    </Typography>
                </Box>
            );

        default:
            return <Typography>Unknown component type</Typography>;
    }
};


const UsageInfoField = ({ record }: { record: FeeTemplate }) => {
    const translate = useTranslate();
    const count = record.usageCount || 0;

    return (
        <Box>
            <Typography variant="body2">
                {translate('resources.fee-templates.show.usedBy', { count })}
            </Typography>
            {count > 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                    {translate('resources.fee-templates.show.usageInfo')}
                </Alert>
            )}
        </Box>
    );
};

// Component to display scheduled changes for this template
const ScheduledChangesSection = ({ templateId }: { templateId: string }) => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [loading, setLoading] = useState(false);
    const [scheduledChanges, setScheduledChanges] = useState<any[]>([]);

    useEffect(() => {
        const fetchScheduledChanges = async () => {
            setLoading(true);
            try {
                const response = await dataProvider.getList('fee-template-changes', {
                    filter: { templateId, status: 'pending' },
                    sort: { field: 'effectiveDate', order: 'ASC' },
                    pagination: { page: 1, perPage: 10 }
                });
                setScheduledChanges(response.data);
            } catch (error) {
                console.error('Failed to fetch scheduled changes:', error);
            } finally {
                setLoading(false);
            }
        };

        if (templateId) {
            fetchScheduledChanges();
        }
    }, [templateId, dataProvider]);

    const handleCancelChange = async (changeId: number) => {
        try {
            await dataProvider.update('fee-template-changes', {
                id: changeId,
                data: { status: 'cancelled' },
                previousData: {} as any
            });
            notify('resources.fee-templates.notifications.changeCancelled', { type: 'success' });
            // Refresh the list
            const response = await dataProvider.getList('fee-template-changes', {
                filter: { templateId, status: 'pending' },
                sort: { field: 'effectiveDate', order: 'ASC' },
                pagination: { page: 1, perPage: 10 }
            });
            setScheduledChanges(response.data);
        } catch (error) {
            notify('resources.fee-templates.notifications.cancelFailed', { type: 'error' });
        }
    };

    if (loading) {
        return (
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (scheduledChanges.length === 0) {
        return null;
    }

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                    <Update sx={{ mr: 2, color: '#00C853' }} />
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            fontSize: { xs: '1rem', sm: '1.25rem' },
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                        }}
                    >
                        {translate('resources.fee-templates.show.scheduledChanges')}
                    </Typography>
                    <Chip
                        label={scheduledChanges.length}
                        size="small"
                        sx={{ ml: 2 }}
                        color="primary"
                    />
                </Box>

                {isMobile ? (
                    <Box sx={{ display: 'grid', gap: 1 }}>
                        {scheduledChanges.map((change) => (
                            <Box key={change.id} sx={mobileDetailRowSx}>
                                <Typography sx={mobileDetailLabelSx}>{translate('resources.fee-templates.fields.effectiveDate')}</Typography>
                                <Typography sx={mobileDetailValueSx}>{new Date(change.effectiveDate).toLocaleString()}</Typography>

                                <Typography sx={{ ...mobileDetailLabelSx, mt: 1 }}>{translate('resources.fee-templates.fields.reason')}</Typography>
                                <Typography sx={mobileDetailValueSx}>{change.reason || '-'}</Typography>

                                <Typography sx={{ ...mobileDetailLabelSx, mt: 1 }}>{translate('resources.fee-templates.fields.createdBy')}</Typography>
                                <Typography sx={mobileDetailValueSx}>{change.createdBy || '-'}</Typography>

                                <Typography sx={{ ...mobileDetailLabelSx, mt: 1 }}>{translate('resources.fee-templates.fields.status')}</Typography>
                                <Chip
                                    label={translate(`resources.fee-templates.status.${change.status}`)}
                                    size="small"
                                    color={change.status === 'pending' ? 'warning' : 'default'}
                                    sx={{
                                        maxWidth: '100%',
                                        '& .MuiChip-label': {
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere',
                                        },
                                    }}
                                />

                                <Button
                                    size="small"
                                    onClick={() => handleCancelChange(change.id)}
                                    color="error"
                                    sx={{ mt: 1, width: '100%' }}
                                >
                                    {translate('ra.action.cancel')}
                                </Button>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        <Table sx={detailsTableSx}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('resources.fee-templates.fields.effectiveDate')}</TableCell>
                                    <TableCell>{translate('resources.fee-templates.fields.reason')}</TableCell>
                                    <TableCell>{translate('resources.fee-templates.fields.createdBy')}</TableCell>
                                    <TableCell>{translate('resources.fee-templates.fields.status')}</TableCell>
                                    <TableCell align="right">{translate('ra.action.actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scheduledChanges.map((change) => (
                                    <TableRow key={change.id}>
                                        <TableCell>{new Date(change.effectiveDate).toLocaleString()}</TableCell>
                                        <TableCell>{change.reason || '-'}</TableCell>
                                        <TableCell>{change.createdBy || '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={translate(`resources.fee-templates.status.${change.status}`)}
                                                size="small"
                                                color={change.status === 'pending' ? 'warning' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                onClick={() => handleCancelChange(change.id)}
                                                color="error"
                                            >
                                                {translate('ra.action.cancel')}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        {translate('resources.fee-templates.show.scheduledChangesInfo')}
                    </Typography>
                </Alert>
            </CardContent>
        </Card>
    );
};

const MobileDeleteSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (record?.isSystem) return null;

    return (
        <Card sx={{ mt: 2, mb: 10, overflow: 'hidden', border: '1px solid', borderColor: 'error.light' }}>
            <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <DeleteOutline sx={{ color: 'error.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'error.main' }}>
                        {translate('resources.fee-templates.show.dangerZone', { _: 'Danger Zone' })}
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {translate('resources.fee-templates.show.deleteWarning', { _: 'This action cannot be undone.' })}
                </Typography>
                <DeleteButton
                    label={translate('ra.action.delete')}
                    variant="outlined"
                    sx={{
                        width: '100%',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                        py: 1.5,
                        borderColor: '#D32F2F',
                        color: '#D32F2F',
                        '&:hover': {
                            borderColor: '#B71C1C',
                            backgroundColor: 'rgba(211, 47, 47, 0.04)',
                        },
                    }}
                />
            </CardContent>
        </Card>
    );
};

const MobileEditFab = () => {
    const record = useRecordContext();
    const navigate = useNavigate();

    if (!record) return null;

    return (
        <Fab
            color="primary"
            onClick={() => navigate(`/fee-templates/${record.id}`)}
            sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                backgroundColor: '#009A75',
                '&:hover': { backgroundColor: '#007A5E' },
                zIndex: 1100,
            }}
        >
            <EditIcon />
        </Fab>
    );
};

export const FeeTemplatesShow = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Box sx={{ position: 'relative' }}>
            <Show
                title={false}
                actions={false}
                component="div"
                sx={{
                    backgroundColor: 'transparent',
                    maxWidth: '100%',
                    '& .RaShow-main': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        maxWidth: '100%',
                    },
                }}
            >
                <SimpleShowLayout
                    sx={{
                        maxWidth: '100%',
                        '& .MuiPaper-root': {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                        },
                        '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                            px: { xs: 0, sm: 2 },
                            py: { xs: 1, sm: 2 },
                        },
                        padding: { xs: 0, sm: 'inherit' },
                    }}
                >
                    <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', minWidth: 0 }}>
                        <PageHeader />
                        {/* Details */}
                        <Box sx={{ minWidth: 0 }}>
                                {/* Basic Information */}
                                <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                            <Info sx={{ mr: 2, color: '#00C853' }} />
                                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                {translate('resources.fee-templates.show.basicInfo')}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                    {translate('resources.fee-templates.fields.currency')}
                                                </Typography>
                                                <Typography variant="body1" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                    <TextField source="currency" />
                                                </Typography>
                                            </Box>

                                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                    {translate('resources.fee-templates.fields.type')}
                                                </Typography>
                                                <FunctionField
                                                    render={(record: any) => (
                                                        <span style={{ pointerEvents: 'none' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                                                                {record.isSystem ? (
                                                                    <>
                                                                        <AdminPanelSettings color="primary" />
                                                                        <Typography sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                                            {translate('resources.fee-templates.fields.systemTemplate')}
                                                                        </Typography>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <PersonOutline />
                                                                        <Typography sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                                            {translate('resources.fee-templates.fields.customTemplate')}
                                                                        </Typography>
                                                                    </>
                                                                )}
                                                            </Box>
                                                        </span>
                                                    )}
                                                />
                                            </Box>
                                        </Box>

                                        <FunctionField
                                            render={(record: any) => {
                                                if (!record?.description) return null;
                                                return (
                                                    <span style={{ pointerEvents: 'none' }}>
                                                        <Box sx={{ mt: 3 }}>
                                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                                {translate('resources.fee-templates.fields.description')}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                                {record.description}
                                                            </Typography>
                                                        </Box>
                                                    </span>
                                                );
                                            }}
                                        />
                                    </CardContent>
                                </Card>

                                {/* Fee Structure */}
                                <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                            <Settings sx={{ mr: 2, color: '#00C853' }} />
                                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                {translate('resources.fee-templates.show.feeStructure')}
                                            </Typography>
                                        </Box>

                                        <FunctionField
                                            render={(record: FeeTemplate) => (
                                                <span style={{ pointerEvents: 'none' }}>
                                                    {record.feeStructure ? (
                                                        <FeeStructureDisplay feeStructure={record.feeStructure} />
                                                    ) : (
                                                        <Alert severity="warning">
                                                            {translate('resources.fee-templates.show.noFeeStructure')}
                                                        </Alert>
                                                    )}
                                                </span>
                                            )}
                                        />
                                    </CardContent>
                                </Card>

                                {/* Scheduled Changes */}
                                <FunctionField
                                    render={(record: FeeTemplate) => (
                                        record?.id ? <ScheduledChangesSection templateId={record.id} /> : null
                                    )}
                                />

                                {/* Metadata */}
                                <Card sx={{ overflow: 'hidden' }}>
                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                            <History sx={{ mr: 2, color: '#00C853' }} />
                                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                {translate('resources.fee-templates.show.metadata')}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                    {translate('resources.fee-templates.fields.createdAt')}
                                                </Typography>
                                                <DateField source="createdAt" showTime />
                                            </Box>

                                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                    {translate('resources.fee-templates.fields.updatedAt')}
                                                </Typography>
                                                <DateField source="updatedAt" showTime />
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>

                                {isMobile && <MobileDeleteSection />}
                        </Box>
                    </Box>
                </SimpleShowLayout>
                {isMobile && <MobileEditFab />}
            </Show>
        </Box>
    );
};
